import { useMemo } from 'react';

import {
  ChakraFlex,
  ChakraSimpleGrid,
  InputGroupV2,
  TagColor,
  TagProps,
  Text,
  UniversalSelect,
} from '@equip.health/ui';

import DateView from '~/components/maud/patients/treatmentEpisode/DateView';
import { TreatmentDetails } from '~/components/maud/patients/treatmentEpisode/Types';
import UniversalSelectTagView from '~/components/maud/patients/treatmentEpisode/UniversalSelectTagView';
import UniversalSelectTextView from '~/components/maud/patients/treatmentEpisode/UniversalSelectTextView';
import { TreatmentEpisodeStatus } from '~/lib/constants/patients.constants';
import {
  levelTagColorMapper,
  treatmentEpisodeColorMapper,
} from '~/lib/util/patient.util';
import {
  useGetTreatmentEpisodeLevelsEnumQuery,
  useGetTreatmentEpisodeStatusReasonsEnumQuery,
  useGetTreatmentEpisodeStatusesEnumQuery,
} from '~/store/services/enumerations';
import { DateTime } from 'luxon';

const TOP_LEVEL_ID = 'treatment-episode-details';
const STATUS_ID = `${TOP_LEVEL_ID}__status`;
const LEVEL_ID = `${TOP_LEVEL_ID}__level`;
const ADMIT_DATE_ID = `${TOP_LEVEL_ID}__admit-date`;
const RELATED_INQUIRY_ID = `${TOP_LEVEL_ID}__related-inquiry`;
const DISCHARGE_DATE_ID = `${TOP_LEVEL_ID}__discharge-date`;
const DISCHARGE_REASON_ID = `${TOP_LEVEL_ID}__discharge-reason`;

export interface DetailsProps {
  data: TreatmentDetails;
  isReadOnly: boolean;
  onChange: (update: Partial<TreatmentDetails>) => void;
}

const Details = ({ data, isReadOnly, onChange }: DetailsProps) => {
  const { data: statusEnums } = useGetTreatmentEpisodeStatusesEnumQuery();
  const { data: levelValues } = useGetTreatmentEpisodeLevelsEnumQuery();
  const { data: episodeReasons } =
    useGetTreatmentEpisodeStatusReasonsEnumQuery();

  const statusOptions: TagProps[] = useMemo(
    () =>
      (statusEnums || [])
        .filter(
          ({ description }) =>
            description !== TreatmentEpisodeStatus.PAUSED &&
            description !== TreatmentEpisodeStatus.VOID,
        )
        .map((status) => ({
          color:
            treatmentEpisodeColorMapper[status.description] || TagColor.gray,
          id: `${TOP_LEVEL_ID}__status-${status.code}`,
          value: status.description,
        })),
    [statusEnums],
  );

  const levelOptions: TagProps[] = useMemo(
    () =>
      (levelValues || [])
        .map((level) => level.label)
        .map((level) => ({
          color: levelTagColorMapper[level.toLowerCase()] || TagColor.gray,
          id: `${TOP_LEVEL_ID}__level-${level}`,
          value: level,
        })),
    [levelValues],
  );

  const dischargeReasons: string[] = useMemo(
    () =>
      (episodeReasons || [])
        .filter(({ status }) => status === 'DISCHARGED')
        .map(({ label }) => label),
    [episodeReasons],
  );

  const isDischarged =
    data.statusDescription === TreatmentEpisodeStatus.DISCHARGED;

  return (
    <ChakraFlex direction="column" gap="12px" width="100%">
      <Text color="neutral.primary" id={`${TOP_LEVEL_ID}__header`} variant="h5">
        Treatment details
      </Text>
      <ChakraSimpleGrid columns={2} gap="12px 24px" width="100%">
        <InputGroupV2 id={STATUS_ID} label="Status">
          <UniversalSelectTagView
            id={STATUS_ID}
            isClearable={false}
            isReadOnly={isReadOnly}
            onChange={(value) => onChange({ statusDescription: value })}
            options={statusOptions}
            value={data.statusDescription}
          />
        </InputGroupV2>
        <InputGroupV2 id={LEVEL_ID} label="Level">
          <UniversalSelectTagView
            id={LEVEL_ID}
            isReadOnly={isReadOnly}
            onChange={(value) => onChange({ level: value })}
            options={levelOptions}
            value={data.level}
          />
        </InputGroupV2>
        <InputGroupV2 id={ADMIT_DATE_ID} isRequired label="Admit date">
          <DateView
            id={ADMIT_DATE_ID}
            isReadOnly={isReadOnly}
            onChange={(value) => onChange({ admitDate: value })}
            value={data.admitDate}
          />
        </InputGroupV2>
        <InputGroupV2 id={RELATED_INQUIRY_ID} label="Related inquiry">
          {isReadOnly ? (
            <Text id={RELATED_INQUIRY_ID}>{data.relatedInquiry}</Text>
          ) : (
            // Show a disabled dropdown. This field is not editable at all. Just following the figma design
            <UniversalSelect
              id={RELATED_INQUIRY_ID}
              isDisabled
              isSingleSelect
              onChange={() => {}}
              options={[]}
              value={{ label: data.relatedInquiry }}
            />
          )}
        </InputGroupV2>
      </ChakraSimpleGrid>
      {isDischarged ? (
        <ChakraSimpleGrid columns={2} gap="12px" width="100%">
          <InputGroupV2 id={DISCHARGE_DATE_ID} label="Discharge date">
            <DateView
              id={DISCHARGE_DATE_ID}
              isReadOnly={isReadOnly}
              minDate={
                data.admitDate
                  ? DateTime.fromJSDate(data.admitDate)
                      .plus({ days: 1 })
                      .toJSDate()
                  : undefined
              }
              onChange={(value) => onChange({ dischargeDate: value })}
              value={data.dischargeDate}
            />
          </InputGroupV2>
          <InputGroupV2 id={DISCHARGE_REASON_ID} label="Discharge reason">
            <UniversalSelectTextView
              id={DISCHARGE_REASON_ID}
              isReadOnly={isReadOnly}
              onChange={(value) => onChange({ dischargeReason: value })}
              options={dischargeReasons}
              value={data.dischargeReason}
            />
          </InputGroupV2>
        </ChakraSimpleGrid>
      ) : (
        <></>
      )}
    </ChakraFlex>
  );
};

export default Details;
